import { FC, useEffect, useState } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material';

interface LabelCountModalProps {
  open: boolean;
  // What the field opens with, and what it is re-seeded to each time the modal reopens.
  defaultCount: string;
  onCancel: () => void;
  onConfirm: (labelCount: string) => void;
}

// Digits only, matching the Count field on the page: barcode guns inject e/+/-/. through a
// type=number input, and a label quantity is always a whole number.
const digitsOnly = (value: string): string => value.replace(/\D/g, '');

const LabelCountModal: FC<LabelCountModalProps> = ({ open, defaultCount, onCancel, onConfirm }) => {
  const [labelCount, setLabelCount] = useState<string>(defaultCount);

  // Re-seed each time the modal opens rather than once at mount: the receiver may have corrected
  // Count, or cancelled and come back, and a stale value here prints the wrong number of labels.
  useEffect(() => {
    if (open) setLabelCount(defaultCount);
  }, [open, defaultCount]);

  // Zero is not expressible: a receiving that prints nothing is a decision for the print phase,
  // not something to record as a quantity. The backend enforces the same floor.
  const canConfirm = Number(labelCount) >= 1;

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="xs">
      <DialogTitle>Labels to print</DialogTitle>
      <DialogContent>
        <DialogContentText variant="body2" sx={{ mb: 2 }}>
          How many labels should be printed for this receiving?
        </DialogContentText>
        <TextField
          fullWidth
          autoFocus
          label="Number of labels"
          value={labelCount}
          onChange={(event) => setLabelCount(digitsOnly(event.target.value))}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" disabled={!canConfirm} onClick={() => onConfirm(labelCount)}>
          Submit Receiving
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LabelCountModal;
